// SIDE-EFFECT IMPORT: must be FIRST so analytics.ts's module-top
// captureInitialSearch() runs BEFORE createBrowserRouter initializes
// React Router's history (per Task 6 design). Reddit click-ID
// attribution depends on reading window.location.search before React
// Router can normalize/strip it.
import './lib/analytics';

import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { createBrowserRouter } from 'react-router-dom';
import App from './App';
import { AppProviders } from './AppProviders';
import { queryClient } from './lib/query-client';
import { configureAmplify } from './lib/amplify-client';
import { detectFlipMode } from './features/reveal/detectFlipMode';
import { appRoutes } from './lib/route-config';
import { PACKS_ACTIVE_QUERY_KEY, type Pack } from './features/packs/queries';
import './styles.css';

function readSsgPacks(): Pack[] | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { __SSG_PACKS__?: unknown };
  return Array.isArray(w.__SSG_PACKS__) ? (w.__SSG_PACKS__ as Pack[]) : null;
}

configureAmplify();

if (typeof document !== 'undefined') {
  document.documentElement.dataset['flipMode'] = detectFlipMode();

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element #root not found in index.html');
  }

  // DA-Round-2 BLOCKER fix: Amplify Hosting's SPA fallback rule serves
  // the SAME index.html for ALL non-asset routes. After SSG, that
  // index.html has the home page pre-rendered in <div id="root">. If a
  // user lands directly on a non-home URL (e.g., /pack/xxx from a shared
  // link), the browser parses the home-page DOM, then React Router
  // routes to the correct page and replaces the DOM. Visible flash of
  // home content.
  //
  // To prevent the flash: if pathname != "/" AND root has SSG content,
  // clear the root and use createRoot (no hydration). React Router will
  // resolve to the correct route on its first render.
  const hasSsgContent = rootElement.children.length > 0;
  const isHomePath = window.location.pathname === '/';

  // Seed QueryClient from SSG pack data when on home path AND root has
  // SSG content. Not useful for non-home paths. Uses setQueryData (same
  // mechanism as main-ssg.tsx) for consistency — respects staleTime.
  if (hasSsgContent && isHomePath) {
    const ssgPacks = readSsgPacks();
    if (ssgPacks) {
      queryClient.setQueryData(PACKS_ACTIVE_QUERY_KEY, ssgPacks);
    }
  }

  // DA-Round-2 MINOR fix: createBrowserRouter pulled to named const for
  // readability + debugger-friendliness.
  const router = createBrowserRouter(appRoutes);

  const reactTree = (
    <React.StrictMode>
      <AppProviders queryClient={queryClient}>
        <App router={router} />
      </AppProviders>
    </React.StrictMode>
  );

  if (hasSsgContent && isHomePath) {
    // SSG'd home page, browser landed on `/` — hydrate the pre-rendered DOM.
    hydrateRoot(rootElement, reactTree);
  } else {
    // Either:
    //   - no SSG content (vite dev mode, or prerender script failed open), OR
    //   - SSG present but pathname != "/" (SPA-fallback poisoning). Clear
    //     the SSG content + createRoot so React Router renders the actual
    //     requested route from scratch — no flash.
    if (hasSsgContent && !isHomePath) {
      rootElement.innerHTML = '';
    }
    createRoot(rootElement).render(reactTree);
  }
}
